import { useEffect, useRef, useState } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as adminActions from '../../actions/admin';
import { axiosCaller } from '../../utils/apiCaller';
import { toast } from 'react-toastify';
import { t } from '../../i18n';
import { PdfLayoutConfig } from '../../types/CompanyProfile.type';
import 'react-quill/dist/quill.snow.css';
import { useQuill } from 'react-quilljs';
import './offerSettings.scss';

interface Props {
    actions: any;
    adminActions: any;
    allOfferFields: any[];
    admin: any;
    companyProfile: any;
    buttonLoader: boolean;
}

const OfferSettings = (props: Props) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

    // Dynamic fields
    const [newFieldName, setNewFieldName] = useState('');
    const [editingFieldId, setEditingFieldId] = useState<number | null>(null);
    const [editingFieldName, setEditingFieldName] = useState('');

    // Service templates
    const [transportEnabled, setTransportEnabled] = useState('');
    const [transportDisabled, setTransportDisabled] = useState('');
    const [installationEnabled, setInstallationEnabled] = useState('');
    const [installationDisabled, setInstallationDisabled] = useState('');

    // Offer conditions — single Quill, swap content on tab change
    const [conditionsLocal, setConditionsLocal] = useState('');
    const [conditionsExport, setConditionsExport] = useState('');
    const [conditionsTab, setConditionsTab] = useState<'local' | 'export'>('local');
    const conditionsTabRef = useRef<'local' | 'export'>('local');
    const isSwappingTab = useRef(false);
    const conditionsPopulated = useRef(false);
    const { quill, quillRef } = useQuill();

    // PDF settings
    const [offerIdFormat, setOfferIdFormat] = useState('{id}');
    const [pdfPrimaryColor, setPdfPrimaryColor] = useState('#1e3a5f');
    const [pdfSecondaryColor, setPdfSecondaryColor] = useState('#dce8f5');

    // PDF layout config — defaults are locale-aware (lazy init), user can override and save
    const [layoutConfig, setLayoutConfig] = useState<PdfLayoutConfig>(() => ({
        title: fm('admin.offerSettings.default.title', 'Ofertë'),
        sellerLabel: fm('admin.offerSettings.default.sellerLabel', 'Shitësi'),
        buyerLabel: fm('admin.offerSettings.default.buyerLabel', 'Blerësi'),
        footerText: '',
        sections: [
            { key: 'header', visible: true },
            { key: 'sellerBuyer', visible: true },
            { key: 'products', visible: true },
            { key: 'totals', visible: true },
            { key: 'comment', visible: true },
            { key: 'conditions', visible: true },
        ],
        productColumns: [
            { key: 'position', visible: true, label: fm('admin.offerSettings.default.colPos', 'Pos.') },
            { key: 'articleImage', visible: true, label: fm('admin.offerSettings.default.colArticle', 'Artikulli') },
            { key: 'description', visible: true, label: fm('admin.offerSettings.default.colDescription', 'Përshkrimi') },
            { key: 'quantity', visible: true, label: fm('admin.offerSettings.default.colQuantity', 'Sasia') },
            { key: 'price', visible: true, label: fm('admin.offerSettings.default.colPrice', 'Çmimi') },
            { key: 'total', visible: true, label: fm('admin.offerSettings.default.colTotal', 'Totali') },
            { key: 'note', visible: true, label: fm('admin.offerSettings.default.colNote', 'Shënim') },
        ],
        sellerFields: [
            { key: 'name', visible: true },
            { key: 'phone', visible: true },
            { key: 'email', visible: true },
            { key: 'address', visible: true },
            { key: 'fiscalNumber', visible: true },
        ],
        buyerFields: [
            { key: 'name', visible: true },
            { key: 'project', visible: true },
            { key: 'phone', visible: true },
            { key: 'address', visible: true },
            { key: 'city', visible: true },
        ],
    }));

    // Section/field-name → i18n key maps. Render via <FormattedMessage id={...}/> at the call-site.
    const sectionLabelKeys: Record<string, string> = {
        header: 'admin.offerSettings.section.header',
        sellerBuyer: 'admin.offerSettings.section.sellerBuyer',
        products: 'admin.offerSettings.section.products',
        totals: 'admin.offerSettings.section.totals',
        comment: 'admin.offerSettings.section.comment',
        conditions: 'admin.offerSettings.section.conditions',
    };

    const sellerFieldLabelKeys: Record<string, string> = {
        name: 'admin.offerSettings.sellerField.name',
        phone: 'admin.offerSettings.sellerField.phone',
        email: 'admin.offerSettings.sellerField.email',
        address: 'admin.offerSettings.sellerField.address',
        fiscalNumber: 'admin.offerSettings.sellerField.fiscalNumber',
    };

    const buyerFieldLabelKeys: Record<string, string> = {
        name: 'admin.offerSettings.buyerField.name',
        project: 'admin.offerSettings.buyerField.project',
        phone: 'admin.offerSettings.buyerField.phone',
        address: 'admin.offerSettings.buyerField.address',
        city: 'admin.offerSettings.buyerField.city',
    };

    // On mount
    useEffect(() => {
        props.actions.fetchOfferFields();
        props.adminActions.getOfferConditions();
        props.actions.fetchCompanyProfile();
        // Load service templates
        axiosCaller('GET', 'admin/offer-service-templates').then(res => {
            const templates = res?.data?.data || [];
            templates.forEach((tmpl: any) => {
                if (tmpl.serviceType === 'transport') {
                    setTransportEnabled(tmpl.enabledText || '');
                    setTransportDisabled(tmpl.disabledText || '');
                }
                if (tmpl.serviceType === 'installation') {
                    setInstallationEnabled(tmpl.enabledText || '');
                    setInstallationDisabled(tmpl.disabledText || '');
                }
            });
        });
    }, []);

    // Populate conditions from Redux
    useEffect(() => {
        const local = props.admin?.offerConditions?.conditions || '';
        const exp = props.admin?.offerConditions?.conditionsExport || '';
        setConditionsLocal(local);
        setConditionsExport(exp);
    }, [props.admin?.offerConditions]);

    // Pre-populate Quill editor once with the local tab content
    useEffect(() => {
        if (quill && conditionsLocal && !conditionsPopulated.current) {
            quill.clipboard.dangerouslyPasteHTML(conditionsLocal);
            conditionsPopulated.current = true;
        }
    }, [quill, conditionsLocal]);

    // Listen for text-change — update the active tab's state (uses ref to avoid closure issues)
    useEffect(() => {
        if (!quill) return;
        const handler = () => {
            if (isSwappingTab.current) return;
            const html = quill.root.innerHTML;
            if (conditionsTabRef.current === 'local') {
                setConditionsLocal(html);
            } else {
                setConditionsExport(html);
            }
        };
        quill.on('text-change', handler);
        return () => { quill.off('text-change', handler); };
    }, [quill]);

    // Populate PDF settings from companyProfile. User-saved layoutConfig values win over the locale-aware defaults.
    useEffect(() => {
        if (props.companyProfile) {
            setOfferIdFormat(props.companyProfile.offerIdFormat || '{id}');
            setPdfPrimaryColor(props.companyProfile.pdfPrimaryColor || '#1e3a5f');
            setPdfSecondaryColor(props.companyProfile.pdfSecondaryColor || '#dce8f5');
            if (props.companyProfile.pdfLayoutConfig) {
                setLayoutConfig(prev => ({ ...prev, ...props.companyProfile.pdfLayoutConfig }));
            }
        }
    }, [props.companyProfile]);

    // ── Dynamic Fields functions ──

    function addField() {
        if (!newFieldName.trim()) {
            toast.error(t('admin.offerSettings.dynamicFields.toastFillName'));
            return;
        }
        props.adminActions.createOfferField({ name: newFieldName }).then(() => {
            props.actions.fetchOfferFields();
            setNewFieldName('');
        });
    }

    function startEditField(field: any) {
        setEditingFieldId(field.id);
        setEditingFieldName(field.name);
    }

    function saveEditField() {
        if (!editingFieldName.trim()) {
            toast.error(t('admin.offerSettings.dynamicFields.toastNameEmpty'));
            return;
        }
        props.adminActions.editOfferField(editingFieldId, { name: editingFieldName }).then(() => {
            props.actions.fetchOfferFields();
            setEditingFieldId(null);
            setEditingFieldName('');
        });
    }

    function cancelEdit() {
        setEditingFieldId(null);
        setEditingFieldName('');
    }

    function deleteField(fieldId: number) {
        if (!window.confirm(t('admin.offerSettings.dynamicFields.confirmDelete'))) return;
        axiosCaller('DELETE', `admin/dynamic-offers/${fieldId}`).then(() => {
            toast.success(t('admin.offerSettings.dynamicFields.toastDeleted'));
            props.actions.fetchOfferFields();
        });
    }

    // ── Offer Conditions functions ──

    function switchConditionsTab(tab: 'local' | 'export') {
        if (tab === conditionsTab || !quill) return;
        // Save current editor content to state
        const currentHtml = quill.root.innerHTML;
        if (conditionsTab === 'local') {
            setConditionsLocal(currentHtml);
        } else {
            setConditionsExport(currentHtml);
        }
        // Block text-change handler during content swap
        isSwappingTab.current = true;
        setConditionsTab(tab);
        conditionsTabRef.current = tab;
        const nextHtml = tab === 'local' ? conditionsLocal : conditionsExport;
        quill.root.innerHTML = nextHtml || '';
        isSwappingTab.current = false;
    }

    function saveConditions() {
        // Capture current editor content before saving
        const currentHtml = quill ? quill.root.innerHTML : '';
        const localHtml = conditionsTab === 'local' ? currentHtml : conditionsLocal;
        const exportHtml = conditionsTab === 'export' ? currentHtml : conditionsExport;
        props.adminActions.saveConditionsForOffer(
            { conditions: localHtml, conditionsExport: exportHtml },
            props.admin?.offerConditions?.id
        );
    }

    // ── Service Templates functions ──

    function saveServiceTemplates() {
        axiosCaller('POST', 'admin/offer-service-templates', {
            templates: [
                { serviceType: 'transport', enabledText: transportEnabled, disabledText: transportDisabled },
                { serviceType: 'installation', enabledText: installationEnabled, disabledText: installationDisabled },
            ]
        }).then(() => toast.success(t('admin.offerSettings.services.toastSaved')))
          .catch(() => toast.error(t('admin.toast.somethingWentWrong')));
    }

    // ── PDF Settings functions ──

    function savePdfSettings() {
        const formData = new FormData();
        formData.append('offerIdFormat', offerIdFormat);
        formData.append('pdfPrimaryColor', pdfPrimaryColor);
        formData.append('pdfSecondaryColor', pdfSecondaryColor);
        formData.append('pdfLayoutConfig', JSON.stringify(layoutConfig));
        props.adminActions.updateOfferPdfSettings(formData);
    }

    // Layout config helpers
    function toggleSection(key: string) {
        setLayoutConfig(prev => ({
            ...prev,
            sections: prev.sections.map(s => s.key === key ? { ...s, visible: !s.visible } : s),
        }));
    }

    function toggleProductColumn(key: string) {
        setLayoutConfig(prev => ({
            ...prev,
            productColumns: prev.productColumns.map(c => c.key === key ? { ...c, visible: !c.visible } : c),
        }));
    }

    function updateProductColumnLabel(key: string, label: string) {
        setLayoutConfig(prev => ({
            ...prev,
            productColumns: prev.productColumns.map(c => c.key === key ? { ...c, label } : c),
        }));
    }

    function toggleSellerField(key: string) {
        setLayoutConfig(prev => ({
            ...prev,
            sellerFields: prev.sellerFields.map(f => f.key === key ? { ...f, visible: !f.visible } : f),
        }));
    }

    function toggleBuyerField(key: string) {
        setLayoutConfig(prev => ({
            ...prev,
            buyerFields: prev.buyerFields.map(f => f.key === key ? { ...f, visible: !f.visible } : f),
        }));
    }

    function formatOfferIdPreview(format: string): string {
        const now = new Date();
        const sampleId = '1234';
        return format
            .replace('{dd-MM-yyyy}', `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`)
            .replace('{MM-yyyy}', `${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`)
            .replace('{id}', sampleId)
            .replace('{yyyy}', String(now.getFullYear()))
            .replace('{MM}', String(now.getMonth() + 1).padStart(2, '0'))
            .replace('{dd}', String(now.getDate()).padStart(2, '0'));
    }

    return (
        <div className="admin-page">
            <div className="os-page">
                <div className="os-page-header">
                    <div>
                        <h1 className="os-page-title"><FormattedMessage id="admin.offerSettings.pageTitle" defaultMessage="Konfigurimet e ofertës" /></h1>
                        <p className="os-page-subtitle"><FormattedMessage id="admin.offerSettings.pageSubtitle" defaultMessage="Menaxhoni fushat, kushtet dhe pamjen e ofertës PDF" /></p>
                    </div>
                </div>

                {/* Section 1: Dynamic Fields */}
                <div className="os-card">
                    <div className="os-card-header">
                        <div>
                            <h2><FormattedMessage id="admin.offerSettings.dynamicFields.title" defaultMessage="Fushat dinamike" /></h2>
                            <p className="os-card-desc"><FormattedMessage id="admin.offerSettings.dynamicFields.desc" defaultMessage="Fushat shtesë që shfaqen në formën e ofertës" /></p>
                        </div>
                    </div>
                    <div className="os-card-body">
                        {/* Existing fields as pills */}
                        <div className="os-field-pills">
                            {props.allOfferFields?.map((field: any, idx: number) => (
                                <div key={field.id || idx} className="os-field-pill">
                                    {editingFieldId === field.id ? (
                                        <div className="os-pill-edit">
                                            <input
                                                type="text"
                                                value={editingFieldName}
                                                onChange={(e) => setEditingFieldName(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && saveEditField()}
                                                autoFocus
                                            />
                                            <button type="button" className="os-pill-action save" onClick={saveEditField} title={fm('common.action.save', 'Ruaj')}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                            </button>
                                            <button type="button" className="os-pill-action cancel" onClick={cancelEdit} title={fm('common.action.cancel', 'Anulo')}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <span className="os-pill-name">{field.name}</span>
                                            <button type="button" className="os-pill-action edit" onClick={() => startEditField(field)} title={fm('common.action.edit', 'Ndrysho')}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                            </button>
                                            <button type="button" className="os-pill-action delete" onClick={() => deleteField(field.id)} title={fm('common.action.delete', 'Fshi')}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                            </button>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Add new field inline */}
                        <div className="os-add-field">
                            <input
                                type="text"
                                value={newFieldName}
                                onChange={(e) => setNewFieldName(e.target.value)}
                                placeholder={fm('admin.offerSettings.dynamicFields.placeholder', 'Emri i fushës së re...')}
                                onKeyDown={(e) => e.key === 'Enter' && addField()}
                            />
                            <button type="button" className="os-add-btn" onClick={addField} disabled={!newFieldName.trim()}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                <FormattedMessage id="admin.offerSettings.dynamicFields.btnAdd" defaultMessage="Shto" />
                            </button>
                        </div>

                        {/* Empty state */}
                        {(!props.allOfferFields || props.allOfferFields.length === 0) && (
                            <p className="os-empty-text"><FormattedMessage id="admin.offerSettings.dynamicFields.empty" defaultMessage="Nuk ka fusha të shtuara. Shtoni fushën e parë me formën më sipër." /></p>
                        )}
                    </div>
                </div>

                {/* Section 2: Offer Conditions — Local + Export tabs */}
                <div className="os-card">
                    <div className="os-card-header">
                        <div>
                            <h2><FormattedMessage id="admin.offerSettings.conditions.title" defaultMessage="Kushtet e ofertës" /></h2>
                            <p className="os-card-desc"><FormattedMessage id="admin.offerSettings.conditions.desc" defaultMessage="Kushte të ndryshme për projekte vendore dhe eksport. Zgjidhet automatikisht sipas destinacionit të projektit." /></p>
                        </div>
                        <button type="button" className="os-save-section-btn" onClick={saveConditions} disabled={props.buttonLoader}>
                            {props.buttonLoader
                                ? <FormattedMessage id="admin.offerSettings.btnSaving" defaultMessage="Duke ruajtur..." />
                                : <FormattedMessage id="admin.offerSettings.conditions.btnSave" defaultMessage="Ruaj kushtet" />}
                        </button>
                    </div>
                    <div className="os-card-body">
                        <div className="os-conditions-tabs">
                            <button
                                type="button"
                                className={`os-conditions-tab ${conditionsTab === 'local' ? 'active' : ''}`}
                                onClick={() => switchConditionsTab('local')}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                                <FormattedMessage id="admin.offerSettings.conditions.tabLocal" defaultMessage="Vendore" />
                            </button>
                            <button
                                type="button"
                                className={`os-conditions-tab ${conditionsTab === 'export' ? 'active' : ''}`}
                                onClick={() => switchConditionsTab('export')}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
                                <FormattedMessage id="admin.offerSettings.conditions.tabExport" defaultMessage="Eksport" />
                            </button>
                        </div>
                        <div className="os-quill-wrapper" ref={quillRef} />
                    </div>
                </div>

                {/* Section 3: Service Templates */}
                <div className="os-card">
                    <div className="os-card-header">
                        <div>
                            <h2><FormattedMessage id="admin.offerSettings.services.title" defaultMessage="Shërbimet e ofertës" /></h2>
                            <p className="os-card-desc"><FormattedMessage id="admin.offerSettings.services.desc" defaultMessage="Teksti i parazgjedhur për transport dhe instalim në ofertat e eksportit" /></p>
                        </div>
                        <button type="button" className="os-save-section-btn" onClick={saveServiceTemplates} disabled={props.buttonLoader}>
                            {props.buttonLoader
                                ? <FormattedMessage id="admin.offerSettings.btnSaving" defaultMessage="Duke ruajtur..." />
                                : <FormattedMessage id="admin.offerSettings.services.btnSave" defaultMessage="Ruaj shërbimet" />}
                        </button>
                    </div>
                    <div className="os-card-body">
                        <div className="os-layout-pair">
                            <div className="os-layout-pair-col">
                                <label className="os-subsection-label"><FormattedMessage id="admin.offerSettings.services.transport" defaultMessage="Transport" /></label>
                                <div className="os-service-field">
                                    <span className="os-service-field-label"><FormattedMessage id="admin.offerSettings.services.withTransport" defaultMessage="Me Transport" /></span>
                                    <textarea
                                        className="os-service-textarea"
                                        value={transportEnabled}
                                        onChange={e => setTransportEnabled(e.target.value)}
                                        placeholder={fm('admin.offerSettings.services.phWithTransport', 'Teksti kur oferta përfshin transportin...')}
                                        rows={3}
                                    />
                                </div>
                                <div className="os-service-field">
                                    <span className="os-service-field-label"><FormattedMessage id="admin.offerSettings.services.withoutTransport" defaultMessage="Pa Transport" /></span>
                                    <textarea
                                        className="os-service-textarea"
                                        value={transportDisabled}
                                        onChange={e => setTransportDisabled(e.target.value)}
                                        placeholder={fm('admin.offerSettings.services.phWithoutTransport', 'Teksti kur oferta nuk përfshin transportin...')}
                                        rows={3}
                                    />
                                </div>
                            </div>
                            <div className="os-layout-pair-col">
                                <label className="os-subsection-label"><FormattedMessage id="admin.offerSettings.services.installation" defaultMessage="Instalim" /></label>
                                <div className="os-service-field">
                                    <span className="os-service-field-label"><FormattedMessage id="admin.offerSettings.services.withInstallation" defaultMessage="Me Instalim" /></span>
                                    <textarea
                                        className="os-service-textarea"
                                        value={installationEnabled}
                                        onChange={e => setInstallationEnabled(e.target.value)}
                                        placeholder={fm('admin.offerSettings.services.phWithInstallation', 'Teksti kur oferta përfshin instalimin...')}
                                        rows={3}
                                    />
                                </div>
                                <div className="os-service-field">
                                    <span className="os-service-field-label"><FormattedMessage id="admin.offerSettings.services.withoutInstallation" defaultMessage="Pa Instalim" /></span>
                                    <textarea
                                        className="os-service-textarea"
                                        value={installationDisabled}
                                        onChange={e => setInstallationDisabled(e.target.value)}
                                        placeholder={fm('admin.offerSettings.services.phWithoutInstallation', 'Teksti kur oferta nuk përfshin instalimin...')}
                                        rows={3}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 4: PDF Settings */}
                <div className="os-card">
                    <div className="os-card-header">
                        <div>
                            <h2><FormattedMessage id="admin.offerSettings.pdf.title" defaultMessage="Formati i ofertës PDF" /></h2>
                            <p className="os-card-desc"><FormattedMessage id="admin.offerSettings.pdf.desc" defaultMessage="Personalizoni numrin e ofertës dhe ngjyrat e dokumentit PDF" /></p>
                        </div>
                        <button type="button" className="os-save-section-btn" onClick={savePdfSettings} disabled={props.buttonLoader}>
                            {props.buttonLoader
                                ? <FormattedMessage id="admin.offerSettings.btnSaving" defaultMessage="Duke ruajtur..." />
                                : <FormattedMessage id="admin.offerSettings.btnSaveChanges" defaultMessage="Ruaj ndryshimet" />}
                        </button>
                    </div>
                    <div className="os-card-body">
                        {/* Offer ID Format */}
                        <div className="os-subsection">
                            <label className="os-subsection-label"><FormattedMessage id="admin.offerSettings.pdf.idFormatLabel" defaultMessage="Formati i ID-së" /></label>
                            <p className="os-help-text">
                                <FormattedMessage
                                    id="admin.offerSettings.pdf.idFormatHelp"
                                    defaultMessage="Përdorni {idTag} për numrin e ofertës. Shtoni tekst para ose pas. Për data: {yyyyTag} viti, {mmTag} muaji, {ddTag} dita."
                                    values={{
                                        idTag: <code>{'{id}'}</code>,
                                        yyyyTag: <code>{'{yyyy}'}</code>,
                                        mmTag: <code>{'{MM}'}</code>,
                                        ddTag: <code>{'{dd}'}</code>,
                                    }}
                                />
                            </p>
                            <input
                                type="text"
                                className="os-mono-input"
                                value={offerIdFormat}
                                onChange={(e) => setOfferIdFormat(e.target.value)}
                                placeholder="{id}"
                            />
                            <div className="os-preview-box">
                                <span className="os-preview-label"><FormattedMessage id="admin.offerSettings.pdf.previewLabel" defaultMessage="Pamja paraprake" /></span>
                                <span className="os-preview-value">{formatOfferIdPreview(offerIdFormat)}</span>
                            </div>
                        </div>

                        {/* Colors */}
                        <div className="os-subsection">
                            <label className="os-subsection-label"><FormattedMessage id="admin.offerSettings.pdf.colorsLabel" defaultMessage="Ngjyrat" /></label>
                            <div className="os-color-row">
                                {/* Primary */}
                                <div className="os-color-field">
                                    <div className="os-color-label-row">
                                        <span><FormattedMessage id="admin.offerSettings.pdf.colorPrimary" defaultMessage="Primare" /></span>
                                        {pdfPrimaryColor !== '#1e3a5f' && (
                                            <button type="button" className="os-reset-btn" onClick={() => setPdfPrimaryColor('#1e3a5f')} title={fm('admin.offerSettings.pdf.resetTooltip', 'Kthe në origjinal')}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                                            </button>
                                        )}
                                    </div>
                                    <div className="os-color-input">
                                        <div className="os-swatch" style={{ backgroundColor: pdfPrimaryColor }}>
                                            <input type="color" value={pdfPrimaryColor} onChange={(e) => setPdfPrimaryColor(e.target.value)} />
                                        </div>
                                        <input type="text" className="os-hex" value={pdfPrimaryColor} onChange={(e) => setPdfPrimaryColor(e.target.value)} maxLength={7} />
                                    </div>
                                </div>
                                {/* Secondary */}
                                <div className="os-color-field">
                                    <div className="os-color-label-row">
                                        <span><FormattedMessage id="admin.offerSettings.pdf.colorSecondary" defaultMessage="Sekondare" /></span>
                                        {pdfSecondaryColor !== '#dce8f5' && (
                                            <button type="button" className="os-reset-btn" onClick={() => setPdfSecondaryColor('#dce8f5')} title={fm('admin.offerSettings.pdf.resetTooltip', 'Kthe në origjinal')}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                                            </button>
                                        )}
                                    </div>
                                    <div className="os-color-input">
                                        <div className="os-swatch" style={{ backgroundColor: pdfSecondaryColor }}>
                                            <input type="color" value={pdfSecondaryColor} onChange={(e) => setPdfSecondaryColor(e.target.value)} />
                                        </div>
                                        <input type="text" className="os-hex" value={pdfSecondaryColor} onChange={(e) => setPdfSecondaryColor(e.target.value)} maxLength={7} />
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
                {/* Section 5: PDF Layout Configuration */}
                <div className="os-card">
                    <div className="os-card-header">
                        <div>
                            <h2><FormattedMessage id="admin.offerSettings.layout.title" defaultMessage="Konfigurimi i pamjes PDF" /></h2>
                            <p className="os-card-desc"><FormattedMessage id="admin.offerSettings.layout.desc" defaultMessage="Zgjidhni cilat seksione, kolona dhe fusha shfaqen në dokumentin PDF" /></p>
                        </div>
                        <button type="button" className="os-save-section-btn" onClick={savePdfSettings} disabled={props.buttonLoader}>
                            {props.buttonLoader
                                ? <FormattedMessage id="admin.offerSettings.btnSaving" defaultMessage="Duke ruajtur..." />
                                : <FormattedMessage id="admin.offerSettings.btnSaveChanges" defaultMessage="Ruaj ndryshimet" />}
                        </button>
                    </div>
                    <div className="os-card-body">

                        {/* Row 1: Three text inputs on one line */}
                        <div className="os-subsection">
                            <label className="os-subsection-label"><FormattedMessage id="admin.offerSettings.layout.textLabel" defaultMessage="Teksti" /></label>
                            <div className="os-layout-text-row">
                                <div className="os-layout-text-field">
                                    <span className="os-layout-text-label"><FormattedMessage id="admin.offerSettings.layout.titleField" defaultMessage="Titulli i PDF" /></span>
                                    <input type="text" value={layoutConfig.title} onChange={e => setLayoutConfig(prev => ({ ...prev, title: e.target.value }))} />
                                </div>
                                <div className="os-layout-text-field">
                                    <span className="os-layout-text-label"><FormattedMessage id="admin.offerSettings.layout.sellerLabelField" defaultMessage="Etiketa &quot;Shitësi&quot;" /></span>
                                    <input type="text" value={layoutConfig.sellerLabel} onChange={e => setLayoutConfig(prev => ({ ...prev, sellerLabel: e.target.value }))} />
                                </div>
                                <div className="os-layout-text-field">
                                    <span className="os-layout-text-label"><FormattedMessage id="admin.offerSettings.layout.buyerLabelField" defaultMessage="Etiketa &quot;Blerësi&quot;" /></span>
                                    <input type="text" value={layoutConfig.buyerLabel} onChange={e => setLayoutConfig(prev => ({ ...prev, buyerLabel: e.target.value }))} />
                                </div>
                            </div>
                        </div>

                        {/* Row 2: Sections + Product columns side by side */}
                        <div className="os-layout-pair">
                            <div className="os-layout-pair-col">
                                <label className="os-subsection-label"><FormattedMessage id="admin.offerSettings.layout.sectionsLabel" defaultMessage="Seksionet" /></label>
                                <div className="os-toggle-list">
                                    {layoutConfig.sections.map(section => (
                                        <div key={section.key} className="os-toggle-row">
                                            <span className="os-toggle-name">
                                                {sectionLabelKeys[section.key]
                                                    ? <FormattedMessage id={sectionLabelKeys[section.key]} />
                                                    : section.key}
                                            </span>
                                            <label className="os-switch">
                                                <input type="checkbox" checked={section.visible} onChange={() => toggleSection(section.key)} />
                                                <span className="os-switch-slider" />
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="os-layout-pair-col">
                                <label className="os-subsection-label"><FormattedMessage id="admin.offerSettings.layout.productColumnsLabel" defaultMessage="Kolonat e produkteve" /></label>
                                <div className="os-toggle-list">
                                    {layoutConfig.productColumns.map(col => (
                                        <div key={col.key} className="os-toggle-row">
                                            <input
                                                type="text"
                                                className="os-col-label-input"
                                                value={col.label || ''}
                                                onChange={e => updateProductColumnLabel(col.key, e.target.value)}
                                                disabled={!col.visible}
                                            />
                                            <label className="os-switch">
                                                <input type="checkbox" checked={col.visible} onChange={() => toggleProductColumn(col.key)} />
                                                <span className="os-switch-slider" />
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Row 3: Seller fields + Buyer fields side by side */}
                        <div className="os-layout-pair" style={{marginTop: '20px'}}>
                            <div className="os-layout-pair-col">
                                <label className="os-subsection-label"><FormattedMessage id="admin.offerSettings.layout.sellerFieldsLabel" defaultMessage="Fushat e shitësit" /></label>
                                <div className="os-toggle-list">
                                    {layoutConfig.sellerFields.map(field => (
                                        <div key={field.key} className="os-toggle-row">
                                            <span className="os-toggle-name">
                                                {sellerFieldLabelKeys[field.key]
                                                    ? <FormattedMessage id={sellerFieldLabelKeys[field.key]} />
                                                    : field.key}
                                            </span>
                                            <label className="os-switch">
                                                <input type="checkbox" checked={field.visible} onChange={() => toggleSellerField(field.key)} />
                                                <span className="os-switch-slider" />
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="os-layout-pair-col">
                                <label className="os-subsection-label"><FormattedMessage id="admin.offerSettings.layout.buyerFieldsLabel" defaultMessage="Fushat e blerësit" /></label>
                                <div className="os-toggle-list">
                                    {layoutConfig.buyerFields.map(field => (
                                        <div key={field.key} className="os-toggle-row">
                                            <span className="os-toggle-name">
                                                {buyerFieldLabelKeys[field.key]
                                                    ? <FormattedMessage id={buyerFieldLabelKeys[field.key]} />
                                                    : field.key}
                                            </span>
                                            <label className="os-switch">
                                                <input type="checkbox" checked={field.visible} onChange={() => toggleBuyerField(field.key)} />
                                                <span className="os-switch-slider" />
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        admin: state.admin
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        adminActions: bindActionCreators(adminActions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(OfferSettings as any)));
